import { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router";
import { SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { Helmet } from "react-helmet-async";
import Intercom from "@intercom/messenger-js-sdk";
import ls from "@livesession/browser";
import { useGetCurrentUserQuery } from "./api/users";
import { useGetUserAttorneyQuery } from "./api/attorneys";
import type { Attorney } from "../../api/src/types";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { PageLoading } from "./components/Loading";
import ErrorMessage, { PageErrorContainer } from "./components/Error";
import { formatUserFullName } from "./utils/format";
import config, { AppType, Environment } from "./config";
import LoadToError from "./LoadToError";
import { RudderStackTracker } from "./components/RudderStackTracker";
import { identifyUser, trackEvent, analytics } from "./utils/rudderstack";

// Lazy-load entire route modules per user type.
// Each module contains its own layout, lazy page imports, and route definitions,
// so a customer never downloads admin/attorney code and vice versa.
const CustomerRoutes = lazy(() => import("./routes/customerRoutes"));
const AttorneyRoutes = lazy(() => import("./routes/attorneyRoutes"));
const AdminRoutes = lazy(() => import("./routes/adminRoutes"));

// Auth pages are small and shared across all unauthenticated users
const SigninRedirect = lazy(() => import("./pages/auth/SigninRedirect"));
const Login = lazy(() => import("./pages/auth/Signin"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const OAuthCallback = lazy(() => import("./pages/auth/OAuthCallback"));

function SuspenseRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

function SignedInRoutes({
  attorney,
  isUserAttorney,
  isAdmin,
  isCustomer,
  isUserFetching,
}: {
  attorney: Attorney | undefined;
  isUserAttorney: boolean;
  isAdmin: boolean;
  isCustomer: boolean;
  isUserFetching: boolean;
}) {
  if (isAdmin) {
    return (
      <SuspenseRoute>
        <AdminRoutes />
      </SuspenseRoute>
    );
  }

  if (isUserAttorney && attorney) {
    return (
      <SuspenseRoute>
        <AttorneyRoutes attorney={attorney} />
      </SuspenseRoute>
    );
  }

  if (isCustomer) {
    return (
      <SuspenseRoute>
        <CustomerRoutes />
      </SuspenseRoute>
    );
  }

  return (
    <Routes>
      <Route
        path="/oauth/callback"
        element={
          <PageErrorContainer>
            <SuspenseRoute>
              <OAuthCallback />
            </SuspenseRoute>
          </PageErrorContainer>
        }
      />
      <Route path="*" element={<LoadToError isFetching={isUserFetching} />} />
    </Routes>
  );
}

function NoDbUserRoutes({ isUserFetching }: { isUserFetching: boolean }) {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <SuspenseRoute>
            <Login />
          </SuspenseRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <SuspenseRoute>
            <Signup />
          </SuspenseRoute>
        }
      />
      <Route
        path="/oauth/callback"
        element={
          <PageErrorContainer>
            <SuspenseRoute>
              <OAuthCallback />
            </SuspenseRoute>
          </PageErrorContainer>
        }
      />
      <Route
        path="*"
        element={
          <>
            <SignedIn>
              <LoadToError isFetching={isUserFetching} />
            </SignedIn>
            <SignedOut>
              <SuspenseRoute>
                <SigninRedirect />
              </SuspenseRoute>
            </SignedOut>
          </>
        }
      />
    </Routes>
  );
}

function App() {
  const { isSignedIn } = useAuth();
  const {
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
  } = useGetCurrentUserQuery();
  const hasSignedInDbUser = Boolean(user && isSignedIn);
  const isUserAttorney = user?.userType === "ATTORNEY";
  const isAdmin = user?.userType === "ADMIN";
  const isCustomer = user?.userType === "CUSTOMER";
  const { data: attorney, isLoading: isAttorneyLoading } =
    useGetUserAttorneyQuery(
      user && isSignedIn && isUserAttorney ? user.id : skipToken,
    );

  const lastIdentifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (user && hasSignedInDbUser) {
      Intercom({
        app_id: config.intercomAppId,
        user_id: user.id,
        name: formatUserFullName(user, "full"),
        email: user.email,
        created_at: new Date(user.createdAt).getTime() / 1000,
      });

      if (config.environment !== Environment.DEVELOPMENT) {
        ls.identify({
          name: formatUserFullName(user, "full"),
          email: user.email,
        });
      }

      if (lastIdentifiedUserId.current !== user.id) {
        identifyUser(user.id, {
          email: user.email,
          user_type: user.userType,
          created_at: user.createdAt,
        });
        trackEvent("user_identified", {
          user_id: user.id,
          user_type: user.userType,
        });
        lastIdentifiedUserId.current = user.id;
      }
    } else {
      if (lastIdentifiedUserId.current) {
        analytics.reset();
        lastIdentifiedUserId.current = null;
      }
    }
  }, [hasSignedInDbUser, user]);

  if (isUserLoading || isAttorneyLoading) {
    return <PageLoading />;
  }

  if (hasSignedInDbUser && isUserAttorney && !attorney) {
    return (
      <PageErrorContainer>
        <ErrorMessage>No attorney found</ErrorMessage>
      </PageErrorContainer>
    );
  }

  const isPostnup = config.appType === AppType.POSTNUP;

  return (
    <BrowserRouter>
      <Helmet>
        <title>
          {isPostnup
            ? "Postnup.com - Online Postnuptial Agreements"
            : "HelloPrenup - Online Prenuptial Agreements | Affordable Prenups"}
        </title>
        <meta
          name="description"
          content={
            isPostnup
              ? "Create your postnuptial agreement online with Postnup.com. Affordable, legally valid postnups created by legal professionals."
              : "Create your prenuptial agreement online with HelloPrenup. Affordable, legally valid prenups created by legal professionals."
          }
        />
      </Helmet>
      <RudderStackTracker />
      {hasSignedInDbUser ? (
        <SignedInRoutes
          attorney={attorney}
          isUserAttorney={isUserAttorney}
          isAdmin={isAdmin}
          isCustomer={isCustomer}
          isUserFetching={isUserFetching}
        />
      ) : (
        // No authenticated DB user for the current Clerk session: either signed
        // out, or signed in but still onboarding (e.g. mid-signup/login,
        // including cross-brand email verification). These routes are not gated
        // only on Clerk's signed-in state so setActive() during signup/login
        // does not unmount the auth flow before email verification can finish.
        <NoDbUserRoutes isUserFetching={isUserFetching} />
      )}
    </BrowserRouter>
  );
}

export default App;
